import type { Action, Error as ProtocolError, ErrScope } from "./protocol";

/**
 * Where each `state.errors[]` entry belongs on screen (contract §2.18, §4.9; handoff Tier 2).
 *
 * `scope` IS THE ROUTING KEY, and it is the only one. The contract gives every error a scope plus
 * the id of the thing it is about — `part_id`, `tool_call_id`, `phase`, `component_id` — precisely
 * so a failure appears AT the thing that failed rather than in a pile at the top. A single error
 * list rendered as a banner is the "dump raw backend metadata into the UI" outcome the brief rules
 * out: five phase failures read as five unrelated catastrophes instead of one bad deploy step.
 *
 * EXACTLY ONE PLACE EACH. An error routed to its anchor must NOT also appear in the run rail, or the
 * reader counts one failure twice and cannot tell whether something happened once or twice.
 *
 * ORPHANS GO TO THE RAIL. An error whose scope names an anchor but whose identifying field is
 * missing or does not resolve is a contract violation on the server's side — and swallowing it would
 * make it invisible. So it falls back to the run rail with its subject named as unknown. Being
 * wrongly placed is recoverable; being absent is not.
 */

/** Which field carries the anchor id, per scope. `run` has none — it IS the run. */
const ANCHOR_FIELD: Record<ErrScope, keyof ProtocolError | null> = {
  run: null,
  part: "part_id",
  tool: "tool_call_id",
  phase: "phase",
  component: "component_id",
};

export interface RoutedErrors {
  /** Run-scoped errors, plus any orphan that could not reach its anchor. */
  run: ProtocolError[];
  byPart: Record<string, ProtocolError[]>;
  byTool: Record<string, ProtocolError[]>;
  byPhase: Record<string, ProtocolError[]>;
  byComponent: Record<string, ProtocolError[]>;
}

function push(bucket: Record<string, ProtocolError[]>, key: string, error: ProtocolError): void {
  (bucket[key] ??= []).push(error);
}

export function routeErrors(errors: readonly ProtocolError[]): RoutedErrors {
  const routed: RoutedErrors = { run: [], byPart: {}, byTool: {}, byPhase: {}, byComponent: {} };

  for (const error of errors) {
    const field = ANCHOR_FIELD[error.scope];
    if (field === null) {
      routed.run.push(error);
      continue;
    }
    const anchor = error[field];
    if (typeof anchor !== "string" || !anchor) {
      // The orphan case. Named as such by `anchorSubject` so the rail can say what it is about.
      routed.run.push(error);
      continue;
    }
    switch (error.scope) {
      case "part":
        push(routed.byPart, anchor, error);
        break;
      case "tool":
        push(routed.byTool, anchor, error);
        break;
      case "phase":
        push(routed.byPhase, anchor, error);
        break;
      case "component":
        push(routed.byComponent, anchor, error);
        break;
      default:
        routed.run.push(error);
    }
  }
  return routed;
}

/** True when this error reached the rail because its anchor was missing, not because it is run-wide. */
export function isOrphan(error: ProtocolError): boolean {
  const field = ANCHOR_FIELD[error.scope];
  if (field === null) return false;
  const anchor = error[field];
  return typeof anchor !== "string" || !anchor;
}

/**
 * What the error is about, in the reader's terms.
 *
 * Only reached for errors in the rail, which are run-wide or orphaned. An orphan says its scope
 * plainly instead of naming an id it does not have — "about a component (not identified)" is a
 * true statement; inventing "component 1" would not be.
 */
export function anchorSubject(error: ProtocolError): string | null {
  if (!isOrphan(error)) return null;
  switch (error.scope) {
    case "part":
      return "part of the answer (not identified)";
    case "tool":
      return "a tool call (not identified)";
    case "phase":
      return "a deploy phase (not identified)";
    case "component":
      return "a component (not identified)";
    default:
      return null;
  }
}

/**
 * The user-facing sentence for an error code.
 *
 * WHY FIXED COPY PER CODE, with the server's `message` shown beneath it: §10 caps what a server
 * string may claim, and the codes below are the ones whose recovery a person cannot guess from the
 * message alone. `SF_AUTH_EXPIRED` is the clearest case — the message describes a token, the
 * recovery is "reconnect the org", and only one of those is actionable.
 *
 * A code with no entry falls through to the server's message, which is better than a generic
 * sentence: for `SF_DEPLOY_COMPONENT_FAILURE` the message names the component and the compiler
 * error, and nothing written here could improve on that.
 */
const CODE_HEADLINE: Partial<Record<ProtocolError["code"], string>> = {
  ORG_NOT_INDEXED: "This org has not been indexed yet",
  SF_AUTH_EXPIRED: "The Salesforce connection expired",
  SF_DEPLOY_TIMEOUT: "Salesforce did not finish the deploy in time",
  GRANT_TARGET_MISSING: "The permission target no longer exists",
  MANAGED_PACKAGE_DEFERRED: "Part of this change is in a managed package",
  WRITE_SCOPE_BLOCKED: "This change is outside what was approved",
  PLAN_REVISION_REQUIRES_APPROVAL: "The plan changed and needs approving again",
  BUDGET_EXCEEDED: "The run reached its budget",
  PROVIDER_RATE_LIMITED: "The model provider is rate-limiting requests",
  TOOL_TIMEOUT: "A tool took too long to answer",
  VALIDATION_FAILED: "Validation did not pass",
  INTERACTION_EXPIRED: "A question expired before it was answered",
  MISSING_REQUIRED_EMIT: "The run did not report something it was required to",
  /*
   * `INTERNAL` IS THE ONE CODE WHOSE MESSAGE MUST NEVER BE THE HEADLINE.
   *
   * The fall-through above is right for `SF_DEPLOY_COMPONENT_FAILURE`, where the message names a
   * component and a compiler error. It is exactly wrong here: `INTERNAL` is the backend's
   * catch-all, so its message is written for whoever reads the logs. Seen on screen as the title
   * of a user-facing error: "component mapper raised before an id was assigned" — a sentence about
   * a mapper, addressed to nobody in the room, with no action a reader could take from it.
   *
   * §10 of the frontend rules draws this line: relay a server string only for statuses meaning
   * "understood and rejected"; everything else gets fixed copy, because an arbitrary internal body
   * can put backend internals on screen. The message is not discarded — it drops to `detail`,
   * where it stays available for a bug report without being the first thing anyone reads.
   */
  INTERNAL: "Something went wrong inside Weave",
};

export interface ErrorPresentation {
  /** The heading. The code's own sentence where one exists, else the server's message. */
  headline: string;
  /** The server's message, when it is not already the headline. Never rewritten. */
  detail: string | null;
  /** The server's `hint`, verbatim. */
  hint: string | null;
  /** A button the backend offered: its label and the `state.actions` action it dispatches. */
  action: { label: string; action: Action } | null;
  /**
   * Whether to offer the reader a retry.
   *
   * From `retryable` ALONE. The temptation is to infer it from the code — `TOOL_TIMEOUT` sounds
   * transient — but the server knows whether this instance can be retried and the code does not.
   */
  retryable: boolean;
}

export function presentError(error: ProtocolError): ErrorPresentation {
  const headline = CODE_HEADLINE[error.code];
  const message = typeof error.message === "string" ? error.message.trim() : "";
  const action =
    error.action && typeof error.action.label === "string" && error.action.action
      ? { label: error.action.label, action: error.action.action as Action }
      : null;
  return {
    // No code as a fallback headline: `MISSING_REQUIRED_EMIT` on screen is an internal identifier,
    // not a sentence. An unmapped code with no message gets the one honest generic line.
    headline: headline ?? (message || "Something went wrong during this run"),
    detail: headline ? message || null : null,
    hint: typeof error.hint === "string" && error.hint.trim() ? error.hint.trim() : null,
    action,
    retryable: error.retryable === true,
  };
}
